import React, { useCallback, useMemo, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Collapse, Stack } from "@mui/material";
import { endOfDay, isToday, startOfDay } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { compact } from "lodash";

import useGetFartherEmployees from "@fartherfinance/frontend/api/Entity/hooks/useFartherEmployees";
import useGetServiceTeams from "@fartherfinance/frontend/api/Entity/hooks/useGetServiceTeams";
import useGetTaskClients from "@fartherfinance/frontend/api/Tasks/hooks/persons/useGetTaskClients";
import { GetTasksQueryParamsV3 } from "@fartherfinance/frontend/api/Tasks/requests/getTasks.v3";
import {
  TaskPriority,
  TaskSource,
  TaskStatus,
  TaskTypeV2,
} from "@fartherfinance/frontend/api/Tasks/Types";
import {
  mapTaskPriorityToLabel,
  mapTaskTypeToLabelV2,
} from "@fartherfinance/frontend/api/Tasks/utils/mappings";
import { AdvisorId } from "@fartherfinance/frontend/api/Types";

import { UNKNOWN_USER } from "../../common";
import {
  assigneeDefaultOption,
  mapTaskSourceToLabel,
  TASK_STATUSES_ACTIVE,
  TASK_STATUSES_ALL,
  TASK_STATUSES_COMPLETED,
  TASKS_DEFAULT_QUERY_PARAMS,
} from "../common";
import { useTasksFiltersV3 } from "../contextV3";
import TaskActor from "../TaskActor/TaskActor";
import TasksFilterFormV3 from "../TasksFilterForm/TasksFilterFormV3";
import TaskStatusChip from "../TaskStatusChip/TaskStatusChip";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import CheckboxV2 from "@src/sharedComponents/Checkbox/CheckboxV2";
import DeletableChip from "@src/sharedComponents/DeletableChip/DeletableChip";
import Menu from "@src/sharedComponents/Menu/Menu";
import { MenuOption } from "@src/sharedComponents/Menu/MenuOption";
import Text from "@src/sharedComponents/Typography/Text";

import TasksFilterChip from "./Components/TasksFilterChip/TasksFilterChip";
import TaskStatusQuickSelectV3 from "./Components/TaskStatusQuickSelect/TaskStatusQuickSelectV3";
import {
  dueTodayQuickFilter,
  highPriorityQuickFilter,
  myTasksQuickFilter,
} from "./TasksToolbar.const";
import { determineFilterChipLabelV2 } from "./TasksToolbar.utils";

import styles from "./TasksToolbar.module.css";

interface TasksToolbarProps {
  advisorId: AdvisorId | undefined;
}

const TasksToolbar: React.FC<TasksToolbarProps> = ({ advisorId }) => {
  const {
    enableTaskAdvancedFilteringPills,
    enableTaskPersonV2Endpoints,
    enableTaskServiceTeams,
  } = useFlags();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    filters,
    showTaskStatusPill,
    taskStatusFilterCategory,
    handleShowTaskStatusPill,
    handleChangeQueryParams,
    handleResetQueryParams,
  } = useTasksFiltersV3();

  const auth = useAdvisorRequestAuth();
  const advisorsQuery = useGetFartherEmployees("All", auth);
  const clientsQuery = useGetTaskClients(
    enableTaskPersonV2Endpoints ? "v2" : "v1",
    auth
  );
  const serviceTeamsQuery = useGetServiceTeams(
    enableTaskServiceTeams ? auth : null
  );

  const advisors = useMemo(
    () => advisorsQuery.data?.advisors ?? [],
    [advisorsQuery]
  );
  const clients = useMemo(
    () => clientsQuery.data?.clients ?? [],
    [clientsQuery]
  );
  const serviceTeams = useMemo(
    () => serviceTeamsQuery.data ?? [],
    [serviceTeamsQuery]
  );

  const handleApplyFilters = useCallback(
    (newFilters: GetTasksQueryParamsV3, showTaskStatusPill: boolean): void => {
      handleChangeQueryParams({
        ...newFilters,
        page: TASKS_DEFAULT_QUERY_PARAMS.page,
      });
      handleShowTaskStatusPill(showTaskStatusPill);
    },
    [handleShowTaskStatusPill, handleChangeQueryParams]
  );

  const handleClearAdvancedFilters = (filterKey: string): void => {
    const isMergedFilterKey = filterKey.includes("&");

    if (filterKey === "filterByTaskStatus") {
      handleApplyFilters(
        {
          filterByTaskStatus: TASKS_DEFAULT_QUERY_PARAMS.filterByTaskStatus,
        },
        false
      );
      return;
    }

    if (isMergedFilterKey) {
      const [key1, key2] = filterKey.split("&");

      handleApplyFilters(
        {
          [key1]: undefined,
          [key2]: undefined,
        },
        showTaskStatusPill
      );
    } else {
      handleApplyFilters({ [filterKey]: undefined }, showTaskStatusPill);
    }
  };

  const handleOnClearButtonClick = (): void => {
    const filterByTaskStatus =
      taskStatusFilterCategory === "active"
        ? TASK_STATUSES_ACTIVE
        : taskStatusFilterCategory === "completed"
        ? TASK_STATUSES_COMPLETED
        : TASK_STATUSES_ALL;

    handleResetQueryParams({ filterByTaskStatus });
    handleShowTaskStatusPill(false);
  };

  const quickFilters = useMemo(() => {
    const {
      filterByPersonAssignee,
      filterByTaskPriority,
      filterByDueDateAfter,
      filterByDueDateBefore,
    } = filters;

    return {
      isMyTasksFilterApplied: Boolean(
        advisorId &&
          filterByPersonAssignee &&
          filterByPersonAssignee.length === 1 &&
          filterByPersonAssignee.includes(advisorId)
      ),
      isHighPriorityFilterApplied: Boolean(
        filterByTaskPriority &&
          filterByTaskPriority.length === 1 &&
          filterByTaskPriority.includes(TaskPriority.enum.HIGH)
      ),
      isDueTodayFilterApplied: Boolean(
        filterByDueDateAfter &&
          isToday(new Date(filterByDueDateAfter)) &&
          filterByDueDateBefore &&
          isToday(new Date(filterByDueDateBefore))
      ),
    };
  }, [filters, advisorId]);

  const quickFiltersOptions = useMemo((): MenuOption[] => {
    const {
      isMyTasksFilterApplied,
      isHighPriorityFilterApplied,
      isDueTodayFilterApplied,
    } = quickFilters;

    return [
      {
        label: "My Tasks",
        onClick: () => {
          handleApplyFilters(
            {
              filterByPersonAssignee: isMyTasksFilterApplied
                ? undefined
                : [advisorId!],
              filterByGroupAssignee: undefined,
            },
            showTaskStatusPill
          );
        },
        IconComponent: (
          <CheckboxV2
            className={styles.checkbox}
            checked={isMyTasksFilterApplied}
          />
        ),
      },
      {
        label: "High Priority",
        onClick: () =>
          handleApplyFilters(
            {
              filterByTaskPriority: isHighPriorityFilterApplied
                ? undefined
                : [TaskPriority.enum.HIGH],
            },
            showTaskStatusPill
          ),
        IconComponent: (
          <CheckboxV2
            className={styles.checkbox}
            checked={isHighPriorityFilterApplied}
          />
        ),
      },
      {
        label: "Due Today",
        onClick: () =>
          handleApplyFilters(
            {
              filterByDueDateAfter: isDueTodayFilterApplied
                ? undefined
                : startOfDay(new Date()).toISOString(),
              filterByDueDateBefore: isDueTodayFilterApplied
                ? undefined
                : endOfDay(new Date()).toISOString(),
            },
            showTaskStatusPill
          ),
        IconComponent: (
          <CheckboxV2
            className={styles.checkbox}
            checked={isDueTodayFilterApplied}
          />
        ),
      },
      {
        label: "Advanced filters",
        menuItemClassName: styles.advancedFilters,
        isCloseOnClick: true,
        onClick: () => setIsModalOpen(true),
      },
    ];
  }, [quickFilters, advisorId, handleApplyFilters, showTaskStatusPill]);

  const quickFiltersTags = useMemo(() => {
    const {
      isMyTasksFilterApplied,
      isHighPriorityFilterApplied,
      isDueTodayFilterApplied,
    } = quickFilters;

    return compact([
      isMyTasksFilterApplied && myTasksQuickFilter,
      isHighPriorityFilterApplied && highPriorityQuickFilter,
      isDueTodayFilterApplied && dueTodayQuickFilter,
    ]);
  }, [quickFilters]);

  const advancedFilters = useMemo(() => {
    const {
      // general
      filterByTaskType,
      filterByTaskStatus,
      filterByTaskPriority,

      // misc
      filterByCustodian,
      filterByServiceTeam,
      filterByTaskSource,

      // persons
      filterByPersonAssignee,
      filterByClient,
      filterByAdvisor,

      // dates
      filterByCreatedAfter,
      filterByCreatedBefore,
      filterByUpdatedAfter,
      filterByUpdatedBefore,
      filterByDueDateAfter,
      filterByDueDateBefore,
    } = filters;

    return [
      ...(filterByTaskType
        ? [
            {
              filterKey: "filterByTaskType",
              filterValue: filterByTaskType,
              filterLabel: determineFilterChipLabelV2(
                { key: "filterByTaskType", value: filterByTaskType },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByTaskStatus && showTaskStatusPill
        ? [
            {
              filterKey: "filterByTaskStatus",
              filterValue: filterByTaskStatus,
              filterLabel: determineFilterChipLabelV2(
                { key: "filterByTaskStatus", value: filterByTaskStatus },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByTaskPriority
        ? [
            {
              filterKey: "filterByTaskPriority",
              filterValue: filterByTaskPriority,
              filterLabel: determineFilterChipLabelV2(
                { key: "filterByTaskPriority", value: filterByTaskPriority },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByCustodian
        ? [
            {
              filterKey: "filterByCustodian",
              filterValue: filterByCustodian,
              filterLabel: determineFilterChipLabelV2(
                { key: "filterByCustodian", value: filterByCustodian },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByServiceTeam && enableTaskServiceTeams
        ? [
            {
              filterKey: "filterByServiceTeam",
              filterValue: filterByServiceTeam,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByServiceTeam",
                  value: filterByServiceTeam,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByTaskSource
        ? [
            {
              filterKey: "filterByTaskSource",
              filterValue: filterByTaskSource,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByTaskSource",
                  value: filterByTaskSource,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByPersonAssignee
        ? [
            {
              filterKey: "filterByPersonAssignee",
              filterValue: filterByPersonAssignee,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByPersonAssignee",
                  value: filterByPersonAssignee,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByAdvisor
        ? [
            {
              filterKey: "filterByAdvisor",
              filterValue: filterByAdvisor,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByAdvisor",
                  value: filterByAdvisor,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByClient
        ? [
            {
              filterKey: "filterByClient",
              filterValue: filterByClient,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByClient",
                  value: filterByClient,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByCreatedAfter && !filterByCreatedBefore
        ? [
            {
              filterKey: "filterByCreatedAfter",
              filterValue: filterByCreatedAfter,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByCreatedAfter",
                  value: filterByCreatedAfter,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(!filterByCreatedAfter && filterByCreatedBefore
        ? [
            {
              filterKey: "filterByCreatedBefore",
              filterValue: filterByCreatedBefore,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByCreatedBefore",
                  value: filterByCreatedBefore,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByCreatedAfter && filterByCreatedBefore
        ? [
            {
              filterKey: "filterByCreatedAfter&filterByCreatedBefore",
              filterValue: [],
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByCreatedAfter&filterByCreatedBefore",
                  value: [filterByCreatedAfter, filterByCreatedBefore],
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByUpdatedAfter && !filterByUpdatedBefore
        ? [
            {
              filterKey: "filterByUpdatedAfter",
              filterValue: filterByUpdatedAfter,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByUpdatedAfter",
                  value: filterByUpdatedAfter,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(!filterByUpdatedAfter && filterByUpdatedBefore
        ? [
            {
              filterKey: "filterByUpdatedBefore",
              filterValue: filterByUpdatedBefore,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByUpdatedBefore",
                  value: filterByUpdatedBefore,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByUpdatedAfter && filterByUpdatedBefore
        ? [
            {
              filterKey: "filterByUpdatedAfter&filterByUpdatedBefore",
              filterValue: [],
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByUpdatedAfter&filterByUpdatedBefore",
                  value: [filterByUpdatedAfter, filterByUpdatedBefore],
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByDueDateAfter && !filterByDueDateBefore
        ? [
            {
              filterKey: "filterByDueDateAfter",
              filterValue: filterByDueDateAfter,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByDueDateAfter",
                  value: filterByDueDateAfter,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(!filterByDueDateAfter && filterByDueDateBefore
        ? [
            {
              filterKey: "filterByDueDateBefore",
              filterValue: filterByDueDateBefore,
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByDueDateBefore",
                  value: filterByDueDateBefore,
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
      ...(filterByDueDateAfter && filterByDueDateBefore
        ? [
            {
              filterKey: "filterByDueDateAfter&filterByDueDateBefore",
              filterValue: [],
              filterLabel: determineFilterChipLabelV2(
                {
                  key: "filterByDueDateAfter&filterByDueDateBefore",
                  value: [filterByDueDateAfter, filterByDueDateBefore],
                },
                clients,
                advisors,
                serviceTeams
              ),
            },
          ]
        : []),
    ];
  }, [
    filters,
    clients,
    advisors,
    serviceTeams,
    showTaskStatusPill,
    enableTaskServiceTeams,
  ]);

  const hasFilters = enableTaskAdvancedFilteringPills
    ? advancedFilters.length > 0
    : quickFiltersTags.length > 0;
  const hasMoreThanOneFilter = quickFiltersTags.length > 1;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.actionsContainer}>
          {enableTaskAdvancedFilteringPills && (
            <TaskStatusQuickSelectV3 handleApplyFilters={handleApplyFilters} />
          )}

          <Stack
            direction="row"
            spacing={1}
            sx={
              !enableTaskAdvancedFilteringPills
                ? {
                    justifyContent: "space-between",
                    flexGrow: 1,
                  }
                : undefined
            }
          >
            <Menu
              transformOrigin={{ horizontal: "left", vertical: "top" }}
              options={quickFiltersOptions}
            >
              {({ handleOpenMenu }) => (
                <Button
                  text="Filters"
                  onClick={handleOpenMenu}
                  variant="outlined"
                  buttonType="primary"
                  endIcon={<FilterListIcon className={styles.icon} />}
                  sx={{ height: "30px" }}
                />
              )}
            </Menu>
          </Stack>
        </div>

        <Collapse in={hasFilters}>
          <div className={styles.chipsContainer}>
            <div className={styles.chips}>
              {!enableTaskAdvancedFilteringPills && (
                <>
                  {hasMoreThanOneFilter && (
                    <DeletableChip
                      label="Clear"
                      onPress={() => handleResetQueryParams()}
                      onClear={() => handleResetQueryParams()}
                    />
                  )}
                  {quickFiltersTags.map(({ label, filterKey, filterValue }) => (
                    <DeletableChip
                      key={label}
                      label={label}
                      onPress={() => null}
                      onClear={() =>
                        handleApplyFilters(
                          filterKey.reduce(
                            (acc, key) => ({ ...acc, [key]: filterValue }),
                            {}
                          ),
                          showTaskStatusPill
                        )
                      }
                    />
                  ))}
                </>
              )}

              {enableTaskAdvancedFilteringPills &&
                advancedFilters.map(
                  ({ filterKey, filterValue, filterLabel }) => {
                    return (
                      <TasksFilterChip
                        key={filterKey}
                        label={filterLabel}
                        onClear={() => handleClearAdvancedFilters(filterKey)}
                        values={
                          Array.isArray(filterValue)
                            ? filterValue
                            : [filterValue]
                        }
                      >
                        {(filterValue) => {
                          switch (filterKey) {
                            case "filterByTaskType":
                              return (
                                <Text variant="body">
                                  {mapTaskTypeToLabelV2(
                                    filterValue as TaskTypeV2
                                  )}
                                </Text>
                              );

                            case "filterByTaskPriority":
                              return (
                                <Text variant="body">
                                  {mapTaskPriorityToLabel(
                                    filterValue as TaskPriority
                                  )}
                                </Text>
                              );

                            case "filterByTaskStatus":
                              return (
                                <TaskStatusChip
                                  taskStatus={filterValue as TaskStatus}
                                />
                              );

                            case "filterByCustodian":
                              return <Text variant="body">{filterValue}</Text>;

                            case "filterByTaskSource":
                              return (
                                <Text variant="body">
                                  {mapTaskSourceToLabel(
                                    filterValue as TaskSource
                                  )}
                                </Text>
                              );

                            case "filterByServiceTeam":
                              // Display "None" for null service team
                              if (filterValue === "null") {
                                return <Text variant="body">(None)</Text>;
                              }

                              // Find the service team by ID to display the name
                              const serviceTeam = serviceTeams.find(
                                (team) => team.serviceTeamId === filterValue
                              );

                              return (
                                <Text variant="body">
                                  {serviceTeam ? serviceTeam.name : filterValue}
                                </Text>
                              );

                            case "filterByClient":
                              const client = clients.find(
                                ({ id }) => id === filterValue
                              );

                              return client ? (
                                <TaskActor
                                  actorName={{
                                    first: client.name.first,
                                    last: client.name.last,
                                  }}
                                />
                              ) : (
                                <TaskActor groupName={UNKNOWN_USER} />
                              );

                            case "filterByPersonAssignee":
                            case "filterByAdvisor":
                              const advisor = advisors.find(
                                ({ advisorId }) => advisorId === filterValue
                              );

                              const isUnassigned = filterValue === "null";

                              if (isUnassigned) {
                                return (
                                  <TaskActor
                                    groupName={assigneeDefaultOption.label}
                                  />
                                );
                              }

                              return advisor ? (
                                <TaskActor
                                  avatarSrc={advisor.details.headshotPath}
                                  actorName={{
                                    first: advisor.name.first,
                                    last: advisor.name.last,
                                  }}
                                />
                              ) : (
                                <TaskActor groupName={UNKNOWN_USER} />
                              );

                            default:
                              const _x: never = filterValue as never;
                          }
                        }}
                      </TasksFilterChip>
                    );
                  }
                )}
            </div>

            {enableTaskAdvancedFilteringPills && (
              <Button
                text="Clear"
                size="small"
                variant="outlined"
                buttonType="primary"
                onClick={handleOnClearButtonClick}
                endIcon={<ClearIcon className={styles.icon} />}
              />
            )}
          </div>
        </Collapse>
      </div>

      {isModalOpen && (
        <TasksFilterFormV3
          currentFilters={filters}
          showTaskStatusPill={showTaskStatusPill}
          onSubmit={handleApplyFilters}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default TasksToolbar;
